import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { useTaskService } from '../../../contexts/TaskServiceContext';
import PropertyFields from '../Properties/PropertyFields';
import { MarkdownText } from './cleanMarkdown';
import {
	StatusCircle,
	AreaTag,
	AssigneeChip,
	DueLabel,
	GateBadge,
	SourceIcon,
	accentStyle,
} from './TaskBits';
import { Avatar } from './Avatar';
import ProposedTriagePanel from './ProposedTriagePanel';
import CardMailThread from './CardMailThread';
import { STUDIO_FIELDS } from './spaceFields';
import {
	sourceMeta,
	childProgress,
	formatShortDate,
	PEOPLE_BY_UID,
	MENTION_BY_TOKEN,
	extractMentions,
	unresolvedClaudeMentions,
	applyShowCompleted,
} from './taskModel';

// Comment text with @mentions highlighted. Splits on @token and wraps tokens
// that are on the mention rail (people + 'claude'); anything else renders as
// plain text. Purely presentational — mentions[] on the doc is the data.
// Highlight @mentions inside a plain-text run. Passed to MarkdownText as its
// `decorate` hook so mentions survive markdown parsing instead of competing
// with it.
function decorateMentions(text, key) {
	const parts = String(text || '').split(/(@[a-zA-Z]+)/g);
	if (parts.length === 1) return text;
	return (
		<React.Fragment key={key}>
			{parts.map((part, i) => {
				const token = part.startsWith('@') ? part.slice(1).toLowerCase() : null;
				if (token && MENTION_BY_TOKEN[token]) {
					return (
						<span key={i} className="wt-mention">
							{part}
						</span>
					);
				}
				return part;
			})}
		</React.Fragment>
	);
}

// Comments render the SAME safe markdown subset as descriptions.
// -----------------------------------------------------------------------------
// This used to be mention-highlighting only, so a pasted review — Anthony
// dictates them via Wispr Flow and has it format the stream into a list —
// rendered its `##` and `-` as literal characters. Comments are where reviews,
// agent evidence, and decisions actually live on this queue (the comment stream
// is effectively the changelog), so they get the same treatment the description
// already had: headings, bullets, checklists, bold/italic/code, safe links.
// No new dependency, no dangerouslySetInnerHTML — the parser was already here.
function CommentText({ text }) {
	return <MarkdownText source={text} className="wt-md--comment" decorate={decorateMentions} />;
}

function Prop({ label, children }) {
	return (
		<div className="wt-prop">
			<span className="wt-prop__label">{label}</span>
			<span className="wt-prop__value">{children}</span>
		</div>
	);
}

// Portal Phase C — task detail (C3). Read + quick-edit everything: clean-rendered
// description, all metadata, real nested sub-tasks (advance inline), threaded
// comments, and linked project / lead / student references (shown, not built).
// Mark complete + Edit are the quick actions; the full edit form is TaskForm.
function TaskDetail({
	task,
	children = [],
	projectsById = {},
	now,
	onClose,
	onEdit,
	onAdvance,
	onAddSubtask,
	onOpenChild,
	onLinkStudent,
	// Which studio fields this space offers (see spaceFields.js). Defaults to the
	// full set, so a mount outside a private space is unchanged.
	fields = STUDIO_FIELDS,
	// Global show-completed preference. Applied to the sub-task list only — the
	// progress readout above it still counts every child, so "2/5" holds while
	// three finished children are hidden.
	showCompleted = false,
}) {
	const { currentUser, userProfile } = useAuth();
	// Which space's collection this detail view reads. Defaults to Work when no
	// provider wraps the tree, so every existing mount is unaffected.
	const { subscribeToTaskComments, addTaskComment, updateTask } = useTaskService();
	const [comments, setComments] = useState([]);
	const [draft, setDraft] = useState('');
	const [posting, setPosting] = useState(false);

	// subscribeToTaskComments is referentially stable (the context value is a
	// module-level constant), so listing it cannot re-fire this subscription.
	useEffect(() => {
		if (!task?.id) return undefined;
		return subscribeToTaskComments(task.id, setComments);
	}, [task?.id, subscribeToTaskComments]);

	const prog = childProgress(children);
	// Gated here rather than only at the render sites: the header breadcrumb also
	// reads `project`, and a space without the field should never resolve one.
	const project = fields.project && task.projectId ? projectsById[task.projectId] : null;
	const done = task.status === 'completed';
	const assignees = (task.assigneeUids || [])
		.map((u) => PEOPLE_BY_UID[u]?.name)
		.filter(Boolean);

	const postComment = async () => {
		if (!draft.trim() || posting) return;
		setPosting(true);
		try {
			await addTaskComment(task.id, {
				text: draft.trim(),
				authorName:
					userProfile?.displayName ||
					currentUser?.displayName ||
					currentUser?.email ||
					'Teammate',
				authorId: currentUser?.uid || null,
				// @mentions ride the shared rail (people + the @claude agent flag).
				mentions: extractMentions(draft.trim()),
			});
			setDraft('');
		} catch (e) {
			console.error('Failed to post comment:', e);
		}
		setPosting(false);
	};

	const reprioritize = (p) => updateTask(task.id, { priority: p }).catch(() => {});

	return (
		<div className="wt-overlay" onClick={onClose}>
			<div
				className="wt-sheet"
				onClick={(e) => e.stopPropagation()}
				style={accentStyle(task.area)}>
				<div className="wt-sheet__bar">
					<span className="wt-sheet__eyebrow">
						{project ? `${project.title} · Task` : 'Task'}
					</span>
					<button
						type="button"
						className="wt-iconbtn"
						aria-label="Edit"
						onClick={() => onEdit?.(task)}>
						<i className="fa-sharp fa-solid fa-pen" />
					</button>
					<button
						type="button"
						className="wt-iconbtn"
						aria-label="Close"
						onClick={onClose}>
						<i className="fa-sharp fa-solid fa-xmark" />
					</button>
				</div>

				<div className="wt-sheet__body">
					<div className="wt-detail-grid">
						<div className="wt-detail-main" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
							<div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
								<StatusCircle
									status={task.status}
									onClick={() => onAdvance?.(task)}
								/>
								<h2 className="wt-detail__title">{task.title}</h2>
							</div>

							<div className="wt-detail__actions">
								<button
									type="button"
									className="btn-cyv-teal"
									onClick={() =>
										onAdvance?.(task, done ? 'pending' : 'completed')
									}>
									<i className="fa-sharp fa-solid fa-circle-check" />
									{done ? 'Reopen' : 'Mark complete'}
								</button>
								<button
									type="button"
									className="btn-cyv-ghost"
									onClick={() => onEdit?.(task)}>
									Edit
								</button>
							</div>

							{/* User-defined properties for whichever space this task lives
							    in. Renders NOTHING when the surrounding schema is empty,
							    which is always the case in Work — its rules deliberately
							    have no propertyDefs document. */}
							<PropertyFields task={task} />

							{/* Auto-triage proposal (read-only) — sits above the description
							    because it is the reason an untriaged capture is open. */}
							<ProposedTriagePanel task={task} />

							{/* The Gmail thread an email card was captured from, with the
							    close signal and reply-from-card (drafts only). Renders
							    nothing without captureThreadId or the send-email permission. */}
							<CardMailThread task={task} />

							{task.description && task.description.trim() && (
								<div>
									<div className="wt-section__title">Description</div>
									<MarkdownText source={task.description} />
								</div>
							)}

							{/* Sub-tasks */}
							<div>
								<div className="wt-section__title">
									Sub-tasks
									{prog.total > 0 && (
										<>
											<span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 700 }}>
												{prog.done}/{prog.total}
											</span>
											<span className="wt-progress" style={{ maxWidth: 120, marginLeft: 'auto' }}>
												<span className="wt-progress__fill" style={{ width: `${prog.pct}%` }} />
											</span>
										</>
									)}
								</div>
								<div className="wt-children wt-children--detail" style={{ marginLeft: 0, ...accentStyle(task.area) }}>
									{applyShowCompleted(children, showCompleted).map((c) => (
										<div
											key={c.id}
											className={`wt-child${c.status === 'completed' ? ' is-done' : ''}`}
											role="button"
											tabIndex={0}
											onClick={() => onOpenChild?.(c)}
											onKeyDown={(e) => { if (e.key === 'Enter') onOpenChild?.(c); }}>
											<StatusCircle
												status={c.status}
												size="sm"
												onClick={() => onAdvance?.(c)}
											/>
											<span className="wt-child__title">{c.title}</span>
											<DueLabel task={c} now={now} />
										</div>
									))}
									{!showCompleted && prog.done > 0 && (
										<p className="wt-children__hidden">
											{prog.done} completed{' '}
											{prog.done === 1 ? 'sub-task' : 'sub-tasks'} hidden
										</p>
									)}
								</div>
								<button
									type="button"
									className="wt-subadd"
									onClick={() => onAddSubtask?.(task)}>
									<i className="fa-sharp fa-solid fa-plus" />
									Add sub-task
								</button>
							</div>

							{/* Linked student — studio only. A household or caregiving
							    task has no student to link, and offering the picker
							    there would list real CYV students inside a private
							    space. */}
							{fields.student && (
							<div>
								<div className="wt-section__title">Linked student</div>
								{task.studentName ? (
									<button type="button" className="wt-file" onClick={() => onLinkStudent?.(task)}>
										<i className="fa-sharp-duotone fa-solid fa-graduation-cap wt-file__badge" />
										{task.studentName}
										<span className="wt-ref" style={{ marginLeft: 'auto' }}>Change</span>
									</button>
								) : (
									<button type="button" className="wt-linkrow" onClick={() => onLinkStudent?.(task)}>
										<i className="fa-sharp fa-solid fa-link" />
										Link a student
									</button>
								)}
							</div>
							)}

							{/* Comments */}
							<div>
								<div className="wt-section__title">
									Comments
									{comments.length > 0 && (
										<span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', fontWeight: 700 }}>
											{comments.length}
										</span>
									)}
									{unresolvedClaudeMentions(comments).length > 0 && (
										<span className="wt-claude-flag" title="An @claude mention with no Claude reply yet — the next agent session surfaces it">
											<i className="fa-sharp fa-solid fa-wand-magic-sparkles" />
											Waiting on Claude
										</span>
									)}
								</div>
								{comments.map((c) => (
									<div key={c.id} className="wt-comment">
										<Avatar uid={c.authorId} name={c.authorName} size={30} />
										<div className="wt-comment__body">
											<div className="wt-comment__meta">
												<span className="wt-comment__author">{c.authorName}</span>
												{c.source === 'claude' && (
													<span className="wt-claude-badge" title="Added by Claude">
														<i className="fa-sharp fa-solid fa-wand-magic-sparkles" />
														Claude
													</span>
												)}
												<span className="wt-comment__time">{formatShortDate(c.createdAt)}</span>
											</div>
											<div className="wt-comment__text"><CommentText text={c.text} /></div>
										</div>
									</div>
								))}
								<div className="wt-composer">
									<textarea
										value={draft}
										placeholder="Add a comment…"
										onChange={(e) => setDraft(e.target.value)}
									/>
									<button
										type="button"
										className="btn-cyv-teal"
										disabled={!draft.trim() || posting}
										onClick={postComment}>
										<i className="fa-sharp fa-solid fa-paper-plane" />
									</button>
								</div>
							</div>
						</div>

						{/* Properties rail */}
						<div className="wt-props">
							<Prop label="Status">
								<select
									className="wt-select"
									style={{ width: 'auto', padding: '0.25rem 0.4rem' }}
									value={task.status || 'pending'}
									onChange={(e) => onAdvance?.(task, e.target.value)}>
									<option value="pending">Pending</option>
									<option value="in_progress">In progress</option>
									<option value="completed">Completed</option>
								</select>
							</Prop>
							<Prop label="Area">
								{task.area ? <AreaTag area={task.area} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
							</Prop>
							<Prop label="Priority">
								<select
									className="wt-select"
									style={{ width: 'auto', padding: '0.25rem 0.4rem' }}
									value={task.priority || 'medium'}
									onChange={(e) => reprioritize(e.target.value)}>
									<option value="high">High</option>
									<option value="medium">Medium</option>
									<option value="low">Low</option>
								</select>
							</Prop>
							<Prop label="Effort">
								{task.effort ? task.effort[0].toUpperCase() + task.effort.slice(1) : '—'}
							</Prop>
							<Prop label="Assignees">
								{assignees.length ? <AssigneeChip task={task} size={24} /> : <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>}
							</Prop>
							{fields.project && (
								<Prop label="Project">
									{project ? (
										<span className="wt-ref"><i className="fa-sharp-duotone fa-solid fa-folder" />{project.title}</span>
									) : '—'}
								</Prop>
							)}
							<Prop label="Due">
								{done && task.closedAt ? (
									<span className="wt-done-chip"><i className="fa-sharp fa-solid fa-circle-check" />Done · {formatShortDate(task.closedAt)}</span>
								) : task.deadline ? (
									<DueLabel task={task} now={now} />
								) : '—'}
							</Prop>
							<Prop label="Parked">
								{task.gate ? <GateBadge gate={task.gate} forceLabel /> : (
									<span className="wt-ref"><i className="fa-sharp fa-solid fa-circle-play" />Active</span>
								)}
							</Prop>
							<Prop label="Source">
								<span className="wt-ref"><SourceIcon source={task.source} />{sourceMeta(task.source).label}</span>
							</Prop>
							{(task.leadId || task.notionLeadId) && (
								<Prop label="From">
									<span className="wt-ref"><i className="fa-sharp fa-solid fa-user-plus" />Inquiry</span>
								</Prop>
							)}
							{(task.createdByName || task.source === 'claude') && (
								<Prop label="Created">
									{task.createdByName || '—'}
									{task.source === 'claude' && (
										<span className="wt-claude-badge" title="Added by Claude">
											<i className="fa-sharp fa-solid fa-wand-magic-sparkles" />
											Claude
										</span>
									)}
								</Prop>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default TaskDetail;
